<script lang="ts" setup>
import { ref, reactive, onMounted, onBeforeUnmount } from "vue"
import { useFilteredMenu } from "~/composables/LefMenu"
import type { User } from "~/models/User"
import { ROLES } from "~/common/roles"
import { goToRoute } from "~/common/helpers"

/**
 *
 *  Props and emits section
 *
 *
 */

const emits = defineEmits(["toggleMenu"])

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

const filtered_main_menu = useFilteredMenu()
const user = useSanctumUser<User>()
const can = useCan()
const router = useRouter()
const route = useRoute()
const { isPartnerMode } = usePartnerBranding()

// Clean scroll state management - rebuilt from scratch
const scrollState = reactive({
    isNavVisible: true,
    lastScrollY: 0,
    isScrollingDown: false,
})

const isMobile = ref(false)

/**
 *
 *  Life cicle events
 *
 *
 */

onMounted(() => {
    checkMobile()
    scrollState.lastScrollY = Math.max(
        0,
        window.scrollY || document.documentElement.scrollTop
    )
    window.addEventListener("scroll", handleScroll)
    window.addEventListener("resize", checkMobile)
})

onBeforeUnmount(() => {
    window.removeEventListener("scroll", handleScroll)
    window.removeEventListener("resize", checkMobile)
})

/**
 *
 *  Functions section
 *
 *
 */

const handleScroll = () => {
    if (!isMobile.value) {
        scrollState.isNavVisible = true
        return
    }

    const currentScrollY = Math.max(
        0,
        window.scrollY || document.documentElement.scrollTop
    )

    const scrollDiff = currentScrollY - scrollState.lastScrollY

    const isScrollingDown = scrollDiff > 0
    scrollState.isScrollingDown = isScrollingDown

    if (currentScrollY <= 400 || !isScrollingDown) {
        scrollState.isNavVisible = true
    } else {
        scrollState.isNavVisible = false
    }

    scrollState.lastScrollY = currentScrollY
}

const checkMobile = () => {
    isMobile.value = window.innerWidth < 768
}
</script>
<template>
    <div
        class="tw-w-full md:tw-sticky md:tw-top-0 tw-z-50"
        id="nav"
        :class="{
            'navigation-hidden': !scrollState.isNavVisible && isMobile,
            'navigation-visible': scrollState.isNavVisible || !isMobile,
        }"
    >
        <nav
            class="tw-py-1 tw-bg-black"
            aria-label="navegation admin"
            v-if="
                user &&
                (user?.role?.name == ROLES.ADMIN ||
                    user?.all_permissions.length > 0 ||
                    user?.stores_manager_of.length > 0)
            "
        >
            <div
                class="max-container tw-flex tw-justify-between tw-gap-2 tw-text-white tw-text-sm tw-relative admin-top-menu"
            >
                <div class="tw-flex tw-gap-2 tw-overflow-x-auto">
                    <AdminTopMenu :menu="filtered_main_menu"></AdminTopMenu>
                </div>
                <div>
                    <SelectStore />
                </div>
            </div>
        </nav>
        <div class="tw-flex tw-flex-col">
            <slot name="pre-nav" class="tw-order-last md:tw-order-first"></slot>
            <nav
                class="tw-w-full tw-bg-white md:tw-bg-[#F2F2F2] md:tw-hidden tw-border-b tw-border-b-[#DFDFDF] tw-py-2 tw-order-first md:tw-order-last"
            >
                <div
                    class="max-container tw-flex tw-justify-between tw-items-center tw-relative"
                >
                    <div class="tw-flex tw-items-center tw-gap-4">
                        <!-- Mobile Menu Button -->
                        <button
                            class="md:tw-hidden tw-text-primary hover:tw-text-secondary focus:tw-outline-none tw-cursor-pointer"
                            @click="() => emits('toggleMenu')"
                            style="background: none"
                        >
                            <q-icon name="menu" size="36px"></q-icon>
                        </button>

                        <div class="md:tw-hidden" @click="goToRoute('/')">
                            <ApplicationLogo
                                class="tw-w-[40px] tw-h-[40px] md:tw-w-auto"
                                src="/img/logo-nav.png"
                            />
                        </div>
                    </div>

                    <div
                        class="tw-flex tw-gap-1 md:tw-gap-4 tw-justify-evenly tw-items-center md:tw-hidden"
                    >
                        <ProfileMenu v-if="!isPartnerMode" />
                        <ShopCartButton />
                    </div>
                </div>
            </nav>
        </div>
        <HomeSubHeader class="tw-flex"></HomeSubHeader>
    </div>
</template>
<style>
@media (max-width: 1023px) {
    .q-header:has(.navigation-hidden),
    .navigation-hidden {
        transition: all 600ms;
        transform: translateY(-100%);
    }

    .q-header:has(.navigation-visible),
    .navigation-visible {
        transition: all 600ms;
        transform: translateY(0);
    }
}
</style>
